import React, { useState } from 'react';
import Scheduler from 'devextreme-react/scheduler';
import 'devextreme/dist/css/dx.light.css';
import { formatHoursMinutes } from '../utils/time';
import { diffLocalDateTimeHours, formatSchedulerDate, formatSchedulerTime, parseSchedulerDateTime } from '../utils/localDateTime';

const formatOperationCount = (value) => {
    const safe = Number.isFinite(Number(value)) ? Math.round(Number(value)) : 0;
    return `# ${new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(safe)}`;
};

const getColorForHours = (hours) => {
    // Cap at 10 hours for the deepest blue
    const normalizedHours = Math.min(hours, 10) / 10;
    // Interpolate between medium light blue (rgb(186, 230, 253)) and deep blue (rgb(30, 64, 175))
    const r = Math.round(186 - (186 - 30) * normalizedHours);
    const g = Math.round(230 - (230 - 64) * normalizedHours);
    const b = Math.round(253 - (253 - 175) * normalizedHours);
    return `rgb(${r}, ${g}, ${b})`;
};

const transformActivityData = (data) => data.map((activity, index) => {
    const startDate = parseSchedulerDateTime(activity.from_time);
    const endDate = parseSchedulerDateTime(activity.to_time);
    const wallClockHours = diffLocalDateTimeHours(activity.from_time, activity.to_time);
    const activityPercentage = Math.round((activity.hours / wallClockHours) * 100);

    return {
        id: index,
        text: activity.workcell_name,
        startDate,
        endDate,
        workcellId: activity.workcell_id,
        hours: activity.hours,
        wallClockHours: Math.round(wallClockHours * 10) / 10,
        activityPercentage,
        backgroundColor: getColorForHours(activity.hours),
        work_done: activity.work_done
    };
});

const MonthlyWorkcellScheduler = ({ activities, onDateChange, date }) => {
    const [currentView, setCurrentView] = useState('Week');

    const transformedData = transformActivityData(activities);

    console.log(transformedData)

    const schedulerProps = {
        dataSource: transformedData,
        views: [{
            type: 'timelineMonth',
            name: 'Month',
            groupOrientation: 'vertical',
            firstDayOfWeek: 1,
            cellDuration: 1440,
            rowHeight: 100,
        }],
        currentView,
        currentDate: parseSchedulerDateTime(`${date}T00:00:00`),
        timeZone: 'UTC',
        groups: ['workcellId'],
        onCurrentDateChange: (newDate) => onDateChange(formatSchedulerDate(newDate)),
        editing: {
            allowAdding: false,
            allowUpdating: false,
            allowDeleting: false
        },
        appointmentRender: ({ appointmentData }) => {
            const startTime = formatSchedulerTime(appointmentData.startDate);
            const endTime = formatSchedulerTime(appointmentData.endDate);

            return (
                <div style={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    color: 'white',
                    fontSize: '0.9em',
                    backgroundColor: appointmentData.backgroundColor,
                    padding: '2px'
                }}>
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}>
                        <div style={{ fontWeight: 'bold' }}>
                            {formatHoursMinutes(appointmentData.hours)} / {formatHoursMinutes(appointmentData.wallClockHours)}
                        </div>
                        <div>{appointmentData.activityPercentage}% active</div>
                        <div>{startTime} - {endTime}</div>
                    </div>
                    <div style={{
                        width: '8px',
                        height: '100%',
                        backgroundColor: `rgba(${
                            appointmentData.activityPercentage <= 25
                                ? '255, 0, 0'
                                : appointmentData.activityPercentage <= 50
                                    ? `255, ${Math.round(((appointmentData.activityPercentage-25)/25) * 255)}, 0`
                                    : `${Math.round((1 - (appointmentData.activityPercentage-50)/50) * 255)}, 255, 0`
                        }, ${Math.min(appointmentData.wallClockHours / 10, 1) * 0.9 + 0.1})`,
                        marginLeft: '4px'
                    }} />
                </div>
            );
        },
        resources: [{
            fieldExpr: 'workcellId',
            dataSource: Object.values(transformedData.reduce((acc, curr) => {
                if (!acc[curr.workcellId]) {
                    acc[curr.workcellId] = {
                        id: curr.workcellId,
                        text: curr.text
                    };
                }
                return acc;
            }, {}))
        }],
        adaptivityEnabled: false,
        cellDuration: 1440,
        maxAppointmentsPerCell: 1,
        showAllDayPanel: false,
        crossScrollingEnabled: true,
        showCurrentTimeIndicator: false,
        appointmentTooltipRender: ({ appointmentData }) => (
            <div>
                <p>
                    <a
                        href={`/chart/daily/plannings/${formatSchedulerDate(appointmentData.startDate)}/${appointmentData.workcellId}`}
                        style={{ textDecoration: "none", color: '#1976d2', fontWeight: 'bold' }}
                    >
                        Daily view
                    </a>
                </p>
                <p>Work done: {formatOperationCount(appointmentData.work_done)}</p>
            </div>
        )
    };

    return (
        <div className="monthly-scheduler">
            <style>
                {`
                    .monthly-scheduler .dx-scheduler-timeline .dx-scheduler-date-table-cell {
                        height: 6em !important;
                    }
                    .dx-scheduler-appointment {
                        border: none !important;
                        box-shadow: none !important;
                        background-color: transparent !important;
                        border-radius: 0 !important;
                    }
                    .dx-scheduler-appointment.dx-scheduler-appointment-horizontal {
                        // margin: -1px !important;
                    }
                    .dx-scheduler-appointment .dx-scheduler-appointment-content {
                        // margin: 0 !important;
                        padding: 0 !important;
                    }
                `}
            </style>
            <Scheduler {...schedulerProps} showCurrentViewSwitcher={true} onCurrentViewChange={setCurrentView} />
        </div>
    );
};

export default MonthlyWorkcellScheduler;
